import { useSyncExternalStore } from 'react';

export type ToastTone = 'neutral' | 'positive' | 'negative';
export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  /** W25: support code (`CAP-XXXXXX`) of the API error behind this toast, when the server sent one. */
  ref: string | null;
}

let current: Toast | null = null;
let nextId = 1;
let timer: number | undefined;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/**
 * One toast at a time, bottom of the screen above the nav, auto-hides after 4 s (8 s when it carries
 * a support code, so there is time to copy it). Pass `ref: errorRef(error)` for API failures.
 */
export function showToast(
  message: string,
  options: { tone?: ToastTone; ref?: string | null } = {},
): void {
  const ref = options.ref ?? null;
  current = { id: nextId++, message, tone: options.tone ?? 'neutral', ref };
  window.clearTimeout(timer);
  timer = window.setTimeout(
    () => {
      current = null;
      emit();
    },
    ref ? 8000 : 4000,
  );
  emit();
}

export function useToast(): Toast | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => null,
  );
}
